import axios from 'axios';
import type {
  Organization,
  CreateOrganization,
  UpdateOrganization,
  OrganizationQuery,
} from '../types/organization';
import oktaAuth from '@/config/okta';
import { addTraceparent, attachTraceContext } from './traceContext';

// Paginated response wrapper from v2 API
interface PagedResult<T> {
  items: T[];
  page: number;
  limit: number;
  totalCount: number;
}

const api = axios.create({
  baseURL: '/api', // Always relative - Vite proxy forwards to backend
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token and trace context
api.interceptors.request.use(
  async (config) => {
    addTraceparent(config);
    const accessToken = oktaAuth.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    attachTraceContext(error);
    if (error.response?.status === 401) {
      // Token expired - redirect to sign in
      window.location.href = '/sign-in';
    }
    if (error.response?.status === 403) {
      const code = error.response.data?.code;
      if (code === 'USER_DEACTIVATED' || code === 'ORG_DEACTIVATED' || code === 'NO_ORGANIZATION') {
        // Let the error propagate so fetchProfile's catch block can clear the profile.
        // The router guard will redirect to AccountDeactivated.
        return Promise.reject(error);
      }
      console.error('[API Error] Access denied - insufficient permissions');
    }
    console.error('[API Error]', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const organizationApi = {
  getAll: async (params?: OrganizationQuery): Promise<PagedResult<Organization>> => {
    const response = await api.get<PagedResult<Organization>>('/organizations', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Organization> => {
    const response = await api.get<Organization>(`/organizations/${id}`);
    return response.data;
  },

  create: async (organization: CreateOrganization): Promise<Organization> => {
    const response = await api.post<Organization>('/organizations', organization);
    return response.data;
  },

  update: async (id: number, organization: UpdateOrganization): Promise<Organization> => {
    const response = await api.put<Organization>(`/organizations/${id}`, organization);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/organizations/${id}`);
  },

  searchSalesforceAccounts: async (q: string): Promise<{ id: string; name: string }[]> => {
    const response = await api.get<{ id: string; name: string }[]>('/organizations/salesforce/accounts', { params: { q } });
    return response.data;
  },

  linkSalesforceAccount: async (id: number, salesforceAccountId: string): Promise<{ id: number; name: string; salesforceAccountId: string; salesforceAccountName: string }> => {
    const response = await api.post(`/organizations/${id}/salesforce/account/link`, { salesforceAccountId });
    return response.data;
  },

  syncSalesforceAccount: async (id: number): Promise<{ id: number; name: string; salesforceAccountId: string }> => {
    const response = await api.post(`/organizations/${id}/salesforce/account/sync`);
    return response.data;
  },
};

export default api;
